import { useEffect } from 'react';
import '../useId/index.js';
import styles from './useOverlay2.js';
import useId from '../useId/useId.js';

/**
 * Adds an overlay to an area that obscures visibility and disables background elements.
 * @param {MutableRefObject} containerRef - A ref pointing to the DOM element to be filled by the overlay.
 * @param {boolean} isVisible -  When true, displays overlay and disables all element behind it.
 * @param {React.MouseEventHandler} onClick - A function to be called when the overlay recieves a click event.
 * @param {string[]} ignoreIds - An array of ids belonging to elements that should not become inert when isInert is true.
 * @param {CSSProperties['zIndex']} zIndex - The zIndex of the overlay.
 * @param {string} dataTestId - A string to be added to the overlay element as a data-testid.
 * @param {number} opacity - The opacity of the overlay.
 */
const useOverlay = _ref => {
  let {
    containerRef,
    isVisible,
    onClick,
    ignoreIds = [],
    zIndex,
    dataTestId,
    opacity
  } = _ref;
  const overlayId = useId();
  useEffect(() => {
    var _containerRef$current;
    const containerNode = (_containerRef$current = containerRef === null || containerRef === void 0 ? void 0 : containerRef.current) !== null && _containerRef$current !== void 0 ? _containerRef$current : document.body;
    const overlay = document.createElement('div');
    if (containerNode === document.body) overlay.classList.add(styles.anchoredToBody);
    overlay.id = overlayId;
    overlay.classList.add(styles.overlay);
    if (onClick) overlay.addEventListener('click', onClick);
    if (dataTestId) overlay.dataset.testid = dataTestId;
    if (!isVisible) overlay.classList.add(styles.hidden);
    if (zIndex || zIndex === 0) overlay.style.zIndex = zIndex.toString();
    if (opacity || opacity === 0) overlay.style.opacity = opacity.toString();
    containerNode.appendChild(overlay);
    containerNode.style.position = 'relative';
    return () => {
      containerNode.style.position = 'static';
      containerNode.removeChild(overlay);
    };
  }, [containerRef, overlayId, isVisible, onClick, dataTestId, zIndex, opacity]);
  useEffect(() => {
    var _containerRef$current2;
    const containerNode = (_containerRef$current2 = containerRef === null || containerRef === void 0 ? void 0 : containerRef.current) !== null && _containerRef$current2 !== void 0 ? _containerRef$current2 : document.body;
    const overlayNode = document.getElementById(overlayId);
    if (isVisible) {
      containerNode.classList.add(styles.blockedScroll);
      Array.from(containerNode.children).forEach(child => {
        if (!ignoreIds.includes(child.id) && child.id !== overlayId) child.setAttribute('inert', '');
      });
      overlayNode === null || overlayNode === void 0 || overlayNode.classList.remove(styles.hidden);
    } else {
      containerNode.classList.remove(styles.blockedScroll);
      Array.from(containerNode.children).forEach(child => {
        child.removeAttribute('inert');
      });
      overlayNode === null || overlayNode === void 0 || overlayNode.classList.add(styles.hidden);
    }
    return () => {
      Array.from(containerNode.children).forEach(child => {
        child.removeAttribute('inert');
      });
    };
  }, [containerRef, isVisible, ignoreIds, overlayId]);
};

export { useOverlay as default };
